/**
 * The CPE site (85-, "Extended PoP" in the code): its type from how many
 * routers it has, and its location as the backend serves it. Pure, so the
 * list, the detail and their tests read the same values.
 */

export type CpeSiteType = "HA" | "Standalone";

/** Two routers or more make an HA pair; one (or none yet) is standalone. */
export const cpeSiteType = (routerCount: number): CpeSiteType =>
  routerCount >= 2 ? "HA" : "Standalone";

/** "HA (2 devices)" / "Standalone (1 device)", for the detail header. */
export const cpeSiteTypeLabel = (routerCount: number): string =>
  `${cpeSiteType(routerCount)} (${routerCount} device${
    routerCount === 1 ? "" : "s"
  })`;

/** The ids of a CPE's routers (80-XXXXXX-01, …), for search. */
export const cpeIds = (cpes: { serviceId: string }[]): string[] =>
  cpes.map((cpe) => cpe.serviceId).filter((id) => id && id !== "-");

export interface SiteLocation {
  locationName: string | null;
  address: string | null;
  /** WGS84 decimal degrees; both set or both null. */
  latitude: number | null;
  longitude: number | null;
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const coordinate = (value: unknown): number | null => {
  const number = typeof value === "string" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : null;
};

/**
 * The location fields off a site payload. A backend that predates them sends
 * none, which reads as no location. Half a GPS pair is no position at all.
 */
export const shapeSiteLocation = (raw: unknown): SiteLocation => {
  const site = (raw ?? {}) as Record<string, unknown>;
  const latitude = coordinate(site.latitude);
  const longitude = coordinate(site.longitude);
  const hasPosition = latitude !== null && longitude !== null;
  return {
    locationName: text(site.locationName),
    address: text(site.address),
    latitude: hasPosition ? latitude : null,
    longitude: hasPosition ? longitude : null,
  };
};

/** "19.432608, -99.133209"; null without a position. */
export const formatCoordinates = (
  latitude: number | null,
  longitude: number | null
): string | null =>
  latitude !== null && longitude !== null
    ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
    : null;

/** The position on OpenStreetMap, pinned and zoomed to the street. */
export const openStreetMapUrl = (
  latitude: number | null,
  longitude: number | null
): string | null => {
  if (latitude === null || longitude === null) return null;
  const lat = latitude.toFixed(6);
  const lon = longitude.toFixed(6);
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;
};
